// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/*
 * Writing the recipe back.
 *
 * Three decisions, each of which is the difference between a file that works
 * and a file that looks like it should:
 *
 * 1. THE NEW CHUNKS GO IMMEDIATELY AFTER IHDR. Legal anywhere, but that is
 *    where every writer puts them and where every reader — including the ones
 *    that stop at the image data — will find them.
 * 2. AN EXISTING CHUNK WITH THE SAME KEYWORD IS REPLACED, NOT DUPLICATED. This
 *    is what makes the graft double as a repair: a `workflow` sitting in a zTXt
 *    after IDAT is dropped and rewritten as a tEXt after IHDR, which is the only
 *    form ComfyUI's reader can see.
 * 3. BYTES AFTER IEND ARE DROPPED WHEN THEY WOULD BREAK THE READER. One to
 *    three stray bytes make ComfyUI's reader throw and load nothing; there is
 *    no reason to carry them into a file whose whole purpose is to be read.
 *
 * IDAT is never touched. Every preserved chunk is copied as a byte range
 * including its original checksum, so the image data of the output is the image
 * data of the input — and `verifyOutput` proves that rather than asserting it,
 * by re-reading the output and comparing a freshly COMPUTED checksum of every
 * IDAT chunk on both sides.
 */

import { chunkFlags, parsePng } from '../png/chunks.ts';
import { crc32 } from '../png/crc32.ts';
import { assemblePlan, planRewrite } from '../png/build.ts';
import { chooseTextChunkType, encodeTextChunk, validateKeyword } from '../png/text.ts';
import { isTextChunk } from '../png/text.ts';
import { predictComfyReadable, comfyVerdictNotes, residualReport } from './verify.ts';
import type { GraftChoice, PngParse, VerifyReport } from '../types.ts';

export interface IdatFingerprint {
  count: number;
  /** Each IDAT chunk's length and the checksum of the bytes actually present. */
  marks: Array<[number, number]>;
}

/**
 * A cheap, sound identity check for the image data.
 *
 * The COMPUTED checksum, deliberately, not the one the file declares. The
 * declared value is exactly what a damaged file is wrong about, so comparing
 * declared checksums would report "the image data is identical" for two files
 * that differ — which is precisely the claim this must not get wrong. It costs
 * one pass over the image data on each side and nothing else: no copy, no
 * decode, and neither file has to be held twice.
 */
export function idatFingerprint(parse: PngParse, bytes: Uint8Array): IdatFingerprint {
  const marks: Array<[number, number]> = [];
  for (const chunk of parse.chunks) {
    if (chunk.type !== 'IDAT') continue;
    marks.push([chunk.dataLen, crc32(bytes, chunk.dataStart, chunk.dataStart + chunk.dataLen)]);
  }
  return { count: marks.length, marks };
}

export function sameIdat(a: IdatFingerprint, b: IdatFingerprint): boolean {
  if (a.count !== b.count) return false;
  return a.marks.every(([len, crc], i) => b.marks[i][0] === len && b.marks[i][1] === crc);
}

export interface GraftResult {
  blob: Blob;
  wrote: string[];
  /** Keywords whose old chunk was dropped so the new one could replace it. */
  replaced: string[];
  droppedTrailer: number;
}

export function buildGraft(bytes: Uint8Array, parse: PngParse, choices: GraftChoice[]): GraftResult {
  const insert: Uint8Array[] = [];
  const remove = new Set<number>();
  const wrote: string[] = [];
  const replaced: string[] = [];
  const seen = new Set<string>();

  for (const choice of choices) {
    const problem = validateKeyword(choice.keyword);
    if (problem) throw new Error(`${problem} (“${choice.keyword}”)`);
    if (choice.text.length === 0) throw new Error(`There is nothing to write for “${choice.keyword}”.`);
    if (seen.has(choice.keyword)) continue;
    seen.add(choice.keyword);
    insert.push(encodeTextChunk(choice.keyword, choice.text));
    wrote.push(`${choice.keyword} (${chooseTextChunkType(choice.text)})`);
  }

  // Drop any existing textual chunk carrying a keyword being written, wherever
  // it sits and whatever type it is.
  for (const chunk of parse.chunks) {
    if (!isTextChunk(chunk.type)) continue;
    const data = bytes.subarray(chunk.dataStart, chunk.dataStart + chunk.dataLen);
    let keyword = '';
    for (let i = 0; i < data.length && data[i] !== 0; i++) keyword += String.fromCharCode(data[i]);
    if (seen.has(keyword)) {
      remove.add(chunk.index);
      if (!replaced.includes(keyword)) replaced.push(keyword);
    }
  }

  // A one-to-three-byte trailer makes ComfyUI's reader throw. Anything longer is
  // harmless to it and is kept, because it is somebody else's data.
  const droppedTrailer = parse.trailerLength >= 1 && parse.trailerLength <= 3 ? parse.trailerLength : 0;

  const plan = planRewrite(parse, {
    insertAfterIhdr: insert,
    removeIndices: remove,
    keepTrailer: droppedTrailer === 0,
  });

  return { blob: assemblePlan(bytes, plan), wrote, replaced, droppedTrailer };
}

/**
 * Re-read the bytes that were just written.
 *
 * This runs over the OUTPUT, with a parser that knows nothing about the plan
 * that produced it. Trusting the writer is what the gate exists not to do.
 */
export async function verifyOutput(
  output: Uint8Array,
  name: string,
  before: IdatFingerprint,
  inputBytes: number,
): Promise<VerifyReport> {
  const parse = parsePng(output);
  const comfy = await predictComfyReadable(output, name);
  comfy.notes = comfyVerdictNotes(comfy, parse);
  return {
    chunkCount: parse.chunks.length,
    badCrcCount: parse.chunks.filter((c) => !c.crcOk).length,
    trailerLength: parse.trailerLength,
    residual: residualReport(parse),
    idatIdentical: sameIdat(before, idatFingerprint(parse, output)),
    inputBytes,
    outputBytes: output.length,
    comfy,
  };
}

/** Filename for an output, keeping the extension honest about the container. */
export function artefactName(inputName: string, suffix: string, extension: string): string {
  const base = inputName
    .replace(/\.[A-Za-z0-9]{1,8}$/, '')
    .replace(/[/\\]/g, '-')
    .replace(/[\u0000-\u001f\u007f]/g, '')
    .trim();
  const stem = base.length > 0 ? base.slice(0, 80) : 'image';
  return `${stem}.${suffix}.${extension}`;
}

/**
 * The "after the image data" note, for the chunk table.
 *
 * `afterIdat` is true for every chunk following the FIRST IDAT, which is the
 * right test for a text chunk a reader might never reach — and meaningless for
 * the rest of the IDAT run and for IEND, which are after the first IDAT by
 * definition. Printing it against those rows makes the table read as broken
 * ("image data, after the image data") and buries the one row where it matters.
 */
export function afterIdatNote(type: string, afterIdat: boolean): string {
  if (!afterIdat || type === 'IDAT' || type === 'IEND') return '';
  return ', after the image data';
}

/** Chunk types this tool understands, for the report's chunk table. */
export function describeChunkType(type: string): string {
  const known: Record<string, string> = {
    IHDR: 'image header',
    IDAT: 'image data',
    IEND: 'end of image',
    PLTE: 'palette',
    tRNS: 'transparency',
    pHYs: 'physical pixel dimensions',
    gAMA: 'gamma',
    cHRM: 'chromaticities',
    sRGB: 'sRGB rendering intent',
    iCCP: 'embedded colour profile',
    tIME: 'last-modified time',
    tEXt: 'text',
    zTXt: 'compressed text',
    iTXt: 'international text',
    eXIf: 'EXIF block',
    caBX: 'C2PA content credentials',
    comf: 'ComfyUI private text',
    acTL: 'animation control',
    fcTL: 'frame control',
    fdAT: 'frame data',
  };
  if (known[type]) return known[type];
  const flags = chunkFlags(type);
  return flags.ancillary ? 'unrecognised, optional' : 'unrecognised, required by its writer';
}
